'use client';

import { memo, useMemo } from 'react';
import { getPlantById } from '@modules/garden-planner/business_logic';
import { useGardenPlannerContext } from '@modules/garden-planner/context/garden_planner_context';
import type { GardenPlannerViews } from '@modules/garden-planner/garden_planner_views';
import type { PlantOption } from '@modules/garden-planner/garden_planner_views';

interface PlantSelectorContainerProps {
  views: GardenPlannerViews;
  selectedPlant: string | null;
  onPlantSelect: (plantId: string | null) => void;
  plantCounts: Record<string, number>;
  showTitle?: boolean;
  autoOpen?: boolean;
}

function PlantSelectorContainerInner({
  views,
  selectedPlant,
  onPlantSelect,
  plantCounts,
  showTitle = false,
  autoOpen = false,
}: PlantSelectorContainerProps) {
  const { plants } = useGardenPlannerContext();
  const { PlantSelectorView } = views;

  const plantOptions = useMemo<PlantOption[]>(
    () =>
      plants.map((plant) => ({
        value: plant.id,
        label: plant.name,
        speciesName:
          plant.speciesName && plant.speciesName !== plant.name ? plant.speciesName : undefined,
        plant,
      })),
    [plants]
  );

  const selectedPlantData = useMemo(
    () => (selectedPlant ? getPlantById(plants, selectedPlant) : null),
    [plants, selectedPlant]
  );

  return (
    <PlantSelectorView
      plantOptions={plantOptions}
      selectedPlant={selectedPlant}
      selectedPlantData={selectedPlantData ?? null}
      plantCounts={plantCounts}
      showTitle={showTitle}
      autoOpen={autoOpen}
      onPlantSelect={onPlantSelect}
    />
  );
}

export const PlantSelectorContainer = memo(PlantSelectorContainerInner);
